import type { Bounds } from '../../../../core/shapes/models/ShapeViewModel';
import type {
  AlignmentGuideCandidate,
  AlignmentGuidesInput,
  AlignmentGuidesOutput,
  AlignmentXAnchor,
  AlignmentYAnchor,
  SnapDelta,
} from '../../models/AlignmentGuides';

type XCandidate = { anchor: AlignmentXAnchor; value: number };
type YCandidate = { anchor: AlignmentYAnchor; value: number };

type XMatch = {
  moving: XCandidate;
  target: XCandidate;
  targetShapeId: string;
  diff: number;
  delta: number;
};

type YMatch = {
  moving: YCandidate;
  target: YCandidate;
  targetShapeId: string;
  diff: number;
  delta: number;
};

const getXAnchors = (bounds: Bounds): XCandidate[] => {
  const left = bounds.x;
  const center = bounds.x + bounds.width / 2;
  const right = bounds.x + bounds.width;
  return [
    { anchor: 'left', value: left },
    { anchor: 'center', value: center },
    { anchor: 'right', value: right },
  ];
};

const getYAnchors = (bounds: Bounds): YCandidate[] => {
  const top = bounds.y;
  const middle = bounds.y + bounds.height / 2;
  const bottom = bounds.y + bounds.height;
  return [
    { anchor: 'top', value: top },
    { anchor: 'middle', value: middle },
    { anchor: 'bottom', value: bottom },
  ];
};

const xAnchorRank: Record<AlignmentXAnchor, number> = { center: 0, left: 1, right: 2 };
const yAnchorRank: Record<AlignmentYAnchor, number> = { middle: 0, top: 1, bottom: 2 };

const pickBetterXMatch = (a: XMatch | null, b: XMatch): XMatch => {
  if (!a) return b;
  if (b.diff < a.diff) return b;
  if (b.diff > a.diff) return a;

  const bMovingRank = xAnchorRank[b.moving.anchor];
  const aMovingRank = xAnchorRank[a.moving.anchor];
  if (bMovingRank < aMovingRank) return b;
  if (bMovingRank > aMovingRank) return a;

  const bTargetRank = xAnchorRank[b.target.anchor];
  const aTargetRank = xAnchorRank[a.target.anchor];
  if (bTargetRank < aTargetRank) return b;
  if (bTargetRank > aTargetRank) return a;

  if (b.targetShapeId < a.targetShapeId) return b;
  if (b.targetShapeId > a.targetShapeId) return a;

  if (b.target.value < a.target.value) return b;
  return a;
};

const pickBetterYMatch = (a: YMatch | null, b: YMatch): YMatch => {
  if (!a) return b;
  if (b.diff < a.diff) return b;
  if (b.diff > a.diff) return a;

  const bMovingRank = yAnchorRank[b.moving.anchor];
  const aMovingRank = yAnchorRank[a.moving.anchor];
  if (bMovingRank < aMovingRank) return b;
  if (bMovingRank > aMovingRank) return a;

  const bTargetRank = yAnchorRank[b.target.anchor];
  const aTargetRank = yAnchorRank[a.target.anchor];
  if (bTargetRank < aTargetRank) return b;
  if (bTargetRank > aTargetRank) return a;

  if (b.targetShapeId < a.targetShapeId) return b;
  if (b.targetShapeId > a.targetShapeId) return a;

  if (b.target.value < a.target.value) return b;
  return a;
};

const computeAllXMatches = (moving: Bounds, staticCandidates: AlignmentGuideCandidate[], threshold: number): XMatch[] => {
  const movingCandidates = getXAnchors(moving);
  const matches: XMatch[] = [];

  staticCandidates.forEach((c) => {
    const targets = getXAnchors(c.bounds);
    movingCandidates.forEach((m) => {
      targets.forEach((t) => {
        const diff = Math.abs(m.value - t.value);
        if (diff > threshold) return;
        matches.push({
          moving: m,
          target: t,
          targetShapeId: c.shapeId,
          diff,
          delta: t.value - m.value,
        });
      });
    });
  });

  return matches;
};

const computeAllYMatches = (moving: Bounds, staticCandidates: AlignmentGuideCandidate[], threshold: number): YMatch[] => {
  const movingCandidates = getYAnchors(moving);
  const matches: YMatch[] = [];

  staticCandidates.forEach((c) => {
    const targets = getYAnchors(c.bounds);
    movingCandidates.forEach((m) => {
      targets.forEach((t) => {
        const diff = Math.abs(m.value - t.value);
        if (diff > threshold) return;
        matches.push({
          moving: m,
          target: t,
          targetShapeId: c.shapeId,
          diff,
          delta: t.value - m.value,
        });
      });
    });
  });

  return matches;
};

const makeLineId = (parts: string[]): string => parts.join('|');

export class AlignmentGuidesService {
  static compute(input: AlignmentGuidesInput): AlignmentGuidesOutput {
    const { movingId, movingBounds, staticCandidates, threshold } = input;

    const xMatches = computeAllXMatches(movingBounds, staticCandidates, threshold);
    const yMatches = computeAllYMatches(movingBounds, staticCandidates, threshold);

    const xBest = xMatches.reduce<XMatch | null>((best, next) => pickBetterXMatch(best, next), null);
    const yBest = yMatches.reduce<YMatch | null>((best, next) => pickBetterYMatch(best, next), null);

    const snapDelta: SnapDelta = {
      dx: xBest ? xBest.delta : 0,
      dy: yBest ? yBest.delta : 0,
    };

    const guides: AlignmentGuidesOutput['guides'] = [];
    const eps = Math.max(1e-4, threshold / 6);

    if (xBest) {
      const selected = xMatches
        .filter((m) => m.targetShapeId === xBest.targetShapeId)
        .filter((m) => Math.abs(m.delta - xBest.delta) <= eps);

      selected.sort((a, b) => {
        const ar = xAnchorRank[a.target.anchor] - xAnchorRank[b.target.anchor];
        if (ar !== 0) return ar;
        return a.target.value - b.target.value;
      });

      const seen = new Set<string>();
      selected.forEach((m) => {
        const key = `${m.targetShapeId}|${m.target.anchor}|${m.target.value}`;
        if (seen.has(key)) return;
        seen.add(key);
        guides.push({
          id: makeLineId(['v', m.targetShapeId, m.target.anchor, String(m.target.value), movingId]),
          orientation: 'vertical',
          position: m.target.value,
          source: { shapeId: m.targetShapeId, anchor: m.target.anchor },
        });
      });
    }

    if (yBest) {
      const selected = yMatches
        .filter((m) => m.targetShapeId === yBest.targetShapeId)
        .filter((m) => Math.abs(m.delta - yBest.delta) <= eps);

      selected.sort((a, b) => {
        const ar = yAnchorRank[a.target.anchor] - yAnchorRank[b.target.anchor];
        if (ar !== 0) return ar;
        return a.target.value - b.target.value;
      });

      const seen = new Set<string>();
      selected.forEach((m) => {
        const key = `${m.targetShapeId}|${m.target.anchor}|${m.target.value}`;
        if (seen.has(key)) return;
        seen.add(key);
        guides.push({
          id: makeLineId(['h', m.targetShapeId, m.target.anchor, String(m.target.value), movingId]),
          orientation: 'horizontal',
          position: m.target.value,
          source: { shapeId: m.targetShapeId, anchor: m.target.anchor },
        });
      });
    }

    return { snapDelta, guides };
  }
}

